<template>
  <div
    v-if="showMoveInOutComponent"
    class="move-in-out-component"
  >
    <Collapse
      :title="$t('handover.moveInOut.moveInOutTempReTransaction')"
      :expand="true"
      :withBorder="false"
    >
      <div slot="content">
        <div
          v-if="hasCreateUpdateAccess"
          class="move-in-out__toolbox"
        >
          <div class="move-in-out__button-box float-left rows">
            <HKLButton
              color="orange"
              @click="openMoveInModal"
            >
              {{ $t('handover.moveInOut.addMoveIn') }}
            </HKLButton>
            <HKLButton
              color="darkblue"
              @click="openMoveOutModal"
            >
              {{ $t('handover.moveInOut.addMoveOut') }}
            </HKLButton>

            <HKLButton
              color="orange"
              @click="() => openNewTempReopenModal(MOVE_TYPE.TEMP_ClOSE)"
            >
              {{ $t('handover.moveInOut.addTempClose') }}
            </HKLButton>
            <HKLButton
              color="darkblue"
              @click="() => openNewTempReopenModal(MOVE_TYPE.REOPEN)"
            >
              {{ $t('handover.moveInOut.addReopen') }}
            </HKLButton>
          </div>
        </div>
        <div class="move-in-out__table">
          <CommonTable
            :tableContentLists="tableContents"
            :renderHeaderList="tableHeader"
            :interactionFunc="interactionFunc"
            :defaultOrder="false"
            :defaultOrderKey="'date'"
            :orderMethods="orderMethods"
            :needFilter="false"
            :tableId="tableId"
          />
        </div>
        <AddNewMoveInOutModal
          ref="editMoveModal"
          @afterSave="loadData"
        />
        <AddNewTempReopenModal
          ref="tempReopenModal"
          @onAfterSave="loadData"
        />
      </div>
    </Collapse>
  </div>
</template>

<script>
import { useStoreGetters } from '@/store'
import {
  ref,
  unref,
  getCurrentInstance,
  onMounted,
  computed,
} from 'vue-demi'

import AddNewMoveInOutModal from './AddNewMoveInOutModal'
import AddNewTempReopenModal from './AddNewTempReopenModal'
import CommonTable from '@/controls/CommonTable'
import Collapse from '@/controls/Collapse'
import dateUtil from '@/utils/dateUtil'

import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { generateHotbDisplayName } from '@/modules/ccd/tenancy/utils'
import { listMoveInOuts } from '@/api/tenancies/request'
import { MOVE_TYPE } from './const'
import HKLButton from '@/controls/Button/HKLButton'
import { useRefModal } from '@/controls/Modal/modal.hook'
import i18n from '@/utils/i18n'

export default {
  name: 'TenancyMoveInOut',
  components: {
    CommonTable,
    Collapse,
    AddNewMoveInOutModal,
    AddNewTempReopenModal,
    HKLButton,
  },
  props: {
    tenancyId: [String, Number],
    hotbs: {
      type: Array,
      require: true,
    },
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const { enableTenantProjectsFeature, enableTenancyRecordsFeature } = useStoreGetters('regions', ['enableTenantProjectsFeature', 'enableTenancyRecordsFeature'])
    const showMoveInOutComponent = computed(() => {
      return unref(enableTenantProjectsFeature) || unref(enableTenancyRecordsFeature)
    })
    const hasCreateUpdateAccess = computed(() => {
      return aclHelper.getAccess(ccdPermsTypes.moveInOut.createUpdate)
    })
    const tempReopenModals = useRefModal()
    const tempReopenModalRef = tempReopenModals.modal
    const typeMap = {
      [MOVE_TYPE.MOVE_IN]: i18n.t('handover.moveInOut.moveIn'),
      [MOVE_TYPE.MOVE_OUT]: i18n.t('handover.moveInOut.moveOut'),
      [MOVE_TYPE.TEMP_ClOSE]: i18n.t('handover.moveInOut.temporaryClose'),
      [MOVE_TYPE.REOPEN]: i18n.t('handover.moveInOut.reopen'),
    }
    const formatType = (type) => {
      return typeMap[type]
    }

    const formatDate = (item) => {
      return dateUtil.formatDate(item, false)
    }
    const formatLastUpdated = (lastUpdatedBy, lastUpdatedAt) => {
      return i18n.t('handover.moveInOut.pattern.lastUpdated', [lastUpdatedBy, formatDate(lastUpdatedAt)])
    }
    const formateMoveInOut = (moveInOut) => {
      const hotbs = moveInOut.hotbs.sort(dateUtil.dateSortFunc('transactionDate', false)).map(hotb => {
        const name = generateHotbDisplayName(unref(currentTenancy), hotb)
        const floor = hotb.floor && hotb.floor.length > 0 ? ` (${hotb.floor})` : ''
        return `${name}${floor}`
      }).join(' ||<br/>')
      const remarkAndHotbs = []
      if (moveInOut.businessDate) {
        const typePathMap = {
          MOVE_IN: 'moveInPattern',
          MOVE_OUT: 'moveOutPattern',
        }
        const typePath = typePathMap[moveInOut.type]
        const businessDateByType = `${i18n.t(`handover.moveInOut.${typePath}.businessDate`)}`
        const businessDateString = i18n.t('handover.moveInOut.pattern.businessDateString', [businessDateByType, formatDate(moveInOut.businessDate)])
        remarkAndHotbs.push(businessDateString)
      }
      if (moveInOut.remark) {
        remarkAndHotbs.push(moveInOut.remark)
      }
      if (hotbs) {
        remarkAndHotbs.push(hotbs)
      }
      return {
        id: moveInOut.id,
        type: moveInOut.type,
        date: formatDate(moveInOut.date),
        originDate: `${moveInOut.date}`,
        remarkAndHotbs: remarkAndHotbs.join('<br/>'),
        lastUpdated: formatLastUpdated(moveInOut.updator.displayName, moveInOut.updatedAt),
        allowLink: true,
      }
    }
    const formateTempReopen = (item) => {
      const date = item.type === MOVE_TYPE.TEMP_ClOSE
        ? i18n.t('dateFromTo', [formatDate(item.date), formatDate(item.businessDate)]) : formatDate(item.date)

      return {
        id: item.id,
        type: item.type,
        date: date,
        originDate: `${item.date} ${item.businessDate}`,
        remarkAndHotbs: item.premises + '<br/>' + item.remark,
        lastUpdated: formatLastUpdated(item.updator.displayName, item.updatedAt),
        allowLink: true,
      }
    }
    let moveInOuts = ref([])
    let tableContents = ref([])
    const loadData = async () => {
      moveInOuts.value = await listMoveInOuts(props.tenancyId, { showHotbDetail: true })
      const formatedItems = unref(moveInOuts).map(item => {
        if ([MOVE_TYPE.MOVE_IN, MOVE_TYPE.MOVE_OUT].includes(item.type)) {
          return formateMoveInOut(item)
        } else {
          return formateTempReopen(item)
        }
      })
      tableContents.value = formatedItems
    }
    onMounted(async () => {
      await loadData()
    })
    const openNewTempReopenModal = (type) => {
      unref(tempReopenModalRef).openNewRecord(type)
    }
    const openEditTempReopenModal = (id) => {
      const tempReopenItem = unref(moveInOuts).find(item => item.id === id)
      unref(tempReopenModalRef).openEditRecord(tempReopenItem)
    }
    const openEditModal = (id) => {
      const moveInOut = unref(moveInOuts).find(item => item.id === id)
      proxy.$refs.editMoveModal.initMoveInOut()
      proxy.$refs.editMoveModal.setMoveInOutParams(moveInOut)
      proxy.$refs.editMoveModal.setOption(moveInOut.type)
      proxy.$refs.editMoveModal.open()
    }
    const clickEdit = (item) => {
      if ([MOVE_TYPE.MOVE_IN, MOVE_TYPE.MOVE_OUT].includes(item.type)) {
        openEditModal(item.id)
      } else {
        openEditTempReopenModal(item.id)
      }
    }
    const orderMethods = {
      lastUpdated: (item, order, orderKey) => dateUtil.parse(item.lastUpdated),
      date: (item, order, orderKey) => {
        return item.originDate
      },
    }
    return {
      hasCreateUpdateAccess,
      showMoveInOutComponent,
      tempReopenModal: tempReopenModals.modal,
      clickEdit,
      interactionFunc: {
        editBtn: unref(hasCreateUpdateAccess) ? clickEdit : () => {},
      },
      openNewTempReopenModal,
      openEditTempReopenModal,
      MOVE_TYPE,
      tableContents,
      loadData,
      formatDate,
      formatType,
      orderMethods,
    }
  },
  data () {
    return {
      tableId: `tenancy_move_in_out_${this.$route.params.id}`,
    }
  },
  computed: {
    tableHeader () {
      const header = [
        {
          title: this.$t('handover.moveInOut.header.type'),
          name: 'type',
          formatRule: this.formatType,
        },
        {
          title: this.$t('handover.moveInOut.header.date'),
          name: 'date',
        },
        {
          title: this.$t('handover.moveInOut.header.remarkAndHotbs'),
          name: 'remarkAndHotbs',
          needHover: false,
        },
        {
          title: this.$t('handover.moveInOut.header.lastUpdated'),
          name: 'lastUpdated',
        },
      ]
      this.hasCreateUpdateAccess && header.push({
        name: 'editBtn',
        title: '',
        defaultValue: '',
      })
      return header
    },
  },
  methods: {
    async openMoveInModal () {
      await this.$refs.editMoveModal.initMoveInOut()
      this.$refs.editMoveModal.setOption(MOVE_TYPE.MOVE_IN)
      this.$refs.editMoveModal.open()
    },
    async openMoveOutModal () {
      await this.$refs.editMoveModal.initMoveInOut()
      this.$refs.editMoveModal.setOption(MOVE_TYPE.MOVE_OUT)
      this.$refs.editMoveModal.open()
    },
  },
}
</script>
<style lang="scss" scoped>
.move-in-out-component {
  margin-top: 12px;
  background: white;
  & ::v-deep .title-container {
    padding: 0px 15px;
    box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
  }
  & ::v-deep .content {
    box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);
  }

  & ::v-deep .collapse {
    .default-title {
      font-size: 14px;
    }
  }
}
.move-in-out__toolbox {
  width: 100%;
  height: 64px;
}
.move-in-out__button-box {
  margin: 12px;
  .move-in-btn {
    margin-right: 12px
  }
}
.move-in-out__content {
  height: 200px;
}
.move-in-out__table {
  padding: 15px;
  & ::v-deep .common-table-header-container {
    min-width: 100% !important;
    max-width: 100% !important;
  }
  & ::v-deep .common-table {
    min-width: 100% !important;
    max-width: 100% !important;
    min-height: unset;
    margin: 0;
    .type {
      width: 12%;
    }
    .date {
      width: 15%;
    }
    .lastUpdated {
      width: 20%;
    }
    .remarkAndHotbs {
      width: 47%;
    }
    .editBtn {
      width: 5%;
    }
    .table-list > .editBtn > a::before {
      cursor: pointer;
      content: "\F040";
      display: inline-block;
      font: normal normal normal 14px/1 FontAwesome;
      font-size: inherit;
      text-rendering: auto;
      -webkit-font-smoothing: antialiased;
      font-size: 20px;
      color: #1779ba;
    }
  }
}
</style>
